import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

const advertiserLabel = (item) =>
  item?.advertiserName ||
  (item?.advertiserProfileId
    ? `광고주 #${item.advertiserProfileId}`
    : "광고주 미지정");

const groupByAdvertiser = (items) => {
  if ((items ?? []).some((item) => item?.campaigns || item?.proposals)) {
    return (items ?? [])
      .map((item) => ({
        key: item.advertiserProfileId ?? "unknown",
        advertiserName: advertiserLabel(item),
        items: item.campaigns ?? item.proposals ?? [],
      }))
      .filter((group) => group.items.length > 0);
  }

  const groups = new Map();
  (items ?? []).forEach((item) => {
    const key = item.advertiserProfileId ?? "unknown";
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        advertiserName: advertiserLabel(item),
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });
  return Array.from(groups.values());
};

export default function GroupedCampaignTab({
  list,
  lastItemRef,
  renderItem,
  itemKey,
  countLabel = "캠페인",
  loading = false,
}) {
  const groups = useMemo(() => groupByAdvertiser(list), [list]);
  const [selectedKey, setSelectedKey] = useState(null);
  const selectedGroup =
    groups.find((group) => group.key === selectedKey) ?? groups[0];
  let visibleIndex = 0;

  useEffect(() => {
    if (!groups.length) {
      setSelectedKey(null);
      return;
    }
    if (!groups.some((group) => group.key === selectedKey)) {
      setSelectedKey(groups[0].key);
    }
  }, [groups, selectedKey]);

  if (loading && groups.length === 0) {
    return (
      <Flex justify="center" py="40px">
        <Spinner />
      </Flex>
    );
  }

  if (groups.length === 0) {
    return <Text>검색 결과가 없습니다.</Text>;
  }

  return (
    <Flex flexDir="column" gap="16px">
      <SimpleGrid columns={{ base: 1, lg: 2, "2xl": 3 }} spacing="12px">
        {groups.map((group) => {
          const active = selectedGroup?.key === group.key;
          const billingModels = [
            ...new Set(group.items.map((item) => item.billingModel).filter(Boolean)),
          ];
          return (
            <Button
              key={group.key}
              h="auto"
              minH="86px"
              justifyContent="flex-start"
              textAlign="left"
              bg={active ? "#F5F9FF" : "white"}
              border="1px solid"
              borderColor={active ? "#0054C0" : "#E2E8F0"}
              borderRadius="10px"
              boxShadow={active ? "0 2px 8px rgba(0,84,192,0.12)" : "none"}
              p="16px"
              _hover={{ bg: "#F5F9FF", borderColor: "#BBD6FF" }}
              onClick={() => setSelectedKey(group.key)}
            >
              <Flex w="100%" align="center" justify="space-between" gap="12px">
                <Box minW={0}>
                  <Text
                    fontSize="16px"
                    fontWeight="700"
                    color="#172940"
                    noOfLines={1}
                  >
                    {group.advertiserName}
                  </Text>
                  <HStack mt="8px" spacing="6px" wrap="wrap">
                    <Box
                      px="8px"
                      py="3px"
                      bg="#E9F2FF"
                      color="#0054C0"
                      borderRadius="6px"
                      fontSize="12px"
                      fontWeight="700"
                    >
                      {countLabel} {group.items.length}개
                    </Box>
                    {billingModels.slice(0, 3).map((model) => (
                      <Box
                        key={model}
                        px="8px"
                        py="3px"
                        bg="#F1E9FF"
                        color="#7A39FF"
                        borderRadius="6px"
                        fontSize="12px"
                        fontWeight="700"
                      >
                        {model}
                      </Box>
                    ))}
                  </HStack>
                </Box>
                <Text color={active ? "#0054C0" : "#747F8C"} fontWeight="700">
                  보기
                </Text>
              </Flex>
            </Button>
          );
        })}
      </SimpleGrid>

      <Box>
        <Text fontSize="15px" fontWeight="700" color="#172940" mb="10px">
          {selectedGroup?.advertiserName} {countLabel} 목록
        </Text>
        <VStack spacing="8px" w="100%" align="stretch">
          {(selectedGroup?.items ?? []).map((item) => {
                const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);
                const isLast = visibleIndex === totalItems - 1;
                visibleIndex += 1;
                return (
                  <Box
                    key={itemKey?.(item) ?? item.campaignId}
                    ref={isLast ? lastItemRef : null}
                    w="100%"
                  >
                    {renderItem(item)}
                  </Box>
                );
          })}
        </VStack>
      </Box>
    </Flex>
  );
}
